// BYO-01 routes, grouped so router.tsx gains one import and three entries:
// - `/c/{slug}/subscriptions` (account menu › My subscriptions; every member), tab in `?tab=`;
// - `/c/{slug}/setup/connections` (was Setup › Keys & connections; CONNECTIONS-AREA: it opens
//   Team › Connections at the matching tab, the Setup catalog entry kept as that link);
// - `/c/{slug}/setup/connections/checks` (the old Connection checks page, the same way);
// - `/connect/callback` (the OAuth vendors' redirect target, plain shell).
import { createRoute, redirect, useNavigate } from "@tanstack/react-router";
import { companiesOf, isOperator } from "../features/company";
import type { ConnectionTab } from "../features/connections/connections-area";
import {
  COMPANY_CONNECTION_TABS,
  type CompanyConnectionTab,
} from "../features/subscriptions/company-setup";
import {
  MY_SUBSCRIPTION_TABS,
  MySubscriptions,
  type MySubscriptionTab,
} from "../features/subscriptions/my-subscriptions";
import { ConnectCallback } from "../features/subscriptions/oauth";
import { useCompany, useSession } from "../features/use-feature";
import { validateFocusSearch } from "../focus/focus";
import { PlainShell } from "../shell/app-shell";
import { authedRoute } from "./_authed";
import { companyRoute } from "./c.$slug";
import { setupRoute } from "./c.$slug.setup";

export const mySubscriptionsRoute = createRoute({
  getParentRoute: () => companyRoute,
  path: "subscriptions",
  validateSearch: (search: Record<string, unknown>) => ({
    ...validateFocusSearch(search),
    ...((MY_SUBSCRIPTION_TABS as readonly unknown[]).includes(search.tab) && search.tab !== "ai"
      ? { tab: search.tab as MySubscriptionTab }
      : {}),
  }),
  component: function MySubscriptionsPage() {
    const { api } = mySubscriptionsRoute.useRouteContext();
    const { tab } = mySubscriptionsRoute.useSearch();
    const navigate = useNavigate();
    return (
      <MySubscriptions
        api={api}
        company={useCompany()}
        {...(tab ? { tab } : {})}
        onTab={(next) =>
          void navigate({
            to: ".",
            search: (prev: Record<string, unknown>) => {
              const { tab: _drop, ...rest } = prev;
              return next === "ai" ? rest : { ...rest, tab: next };
            },
          } as never)
        }
      />
    );
  },
});

/**
 * CONNECTIONS-AREA (2026-10-30): Setup › Keys & connections moved to Team › Connections; its URL
 * stays and opens the matching tab (Keys → API keys; Catalog, Connections and Checks → Sign-in
 * apps, where the rows carry health and Check; Access → Access), the focus query kept.
 */
const AREA_TAB: Record<CompanyConnectionTab, ConnectionTab> = {
  keys: "keys",
  catalog: "apps",
  connections: "apps",
  checks: "apps",
  access: "access",
};

function toArea(params: { slug: string }, search: Record<string, unknown>) {
  const { tab, ...rest } = search;
  const old = (COMPANY_CONNECTION_TABS as readonly unknown[]).includes(tab)
    ? (tab as CompanyConnectionTab)
    : "keys";
  const next = AREA_TAB[old];
  return redirect({
    to: "/c/$slug/agents/connections",
    params,
    search: next === "apps" ? rest : { ...rest, tab: next },
    replace: true,
  } as never);
}

export const setupConnectionsRoute = createRoute({
  getParentRoute: () => setupRoute,
  path: "connections",
  beforeLoad: ({ params, search }) => {
    throw toArea(params, search as Record<string, unknown>);
  },
});

/** UX-A 1.5: the old Connection checks page; it opens Team › Connections (health is per row). */
export const setupConnectionChecksRoute = createRoute({
  getParentRoute: () => setupRoute,
  path: "connections/checks",
  beforeLoad: ({ params, search }) => {
    throw toArea(params, { ...(search as Record<string, unknown>), tab: "checks" });
  },
});

export const connectCallbackRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: "connect/callback",
  validateSearch: (s: Record<string, unknown>) => ({
    ...(typeof s.code === "string" ? { code: s.code } : {}),
    ...(typeof s.state === "string" ? { state: s.state } : {}),
    ...(typeof s.error === "string" ? { error: s.error } : {}),
  }),
  component: function ConnectCallbackPage() {
    const { api } = connectCallbackRoute.useRouteContext();
    const search = connectCallbackRoute.useSearch();
    const { me } = useSession();
    const navigate = useNavigate();
    const current = isOperator(me) ? null : (companiesOf(me)[0]?.slug ?? null);
    return (
      <PlainShell me={me} api={api} current={current}>
        <ConnectCallback
          api={api}
          search={search}
          onDone={(slug) =>
            void (slug
              ? navigate({
                  to: "/c/$slug/subscriptions",
                  params: { slug },
                  search: { tab: "connections" },
                } as never)
              : navigate({ to: "/" }))
          }
        />
      </PlainShell>
    );
  },
});
